import { Check, BarChart3, Palette, Printer, ArrowUpRight } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const SERVICES = [
  {
    id: "digital-marketing",
    icon: BarChart3,
    title: "Digital Marketing",
    desc: "Performance ads, SEO, social media growth, and lead generation strategies designed to scale your business.",
    bullets: [
      "High-ROI Brand Campaigns",
      "Targeted Performance Ads",
      "Social Media Engineering",
    ],
  },
  {
    id: "branding-creative",
    icon: Palette,
    title: "Branding & Creative",
    desc: "Brand identity, visual storytelling, content creation, and creative campaigns that build memorable brands.",
    bullets: [
      "Corporate Brand Strategy",
      "Video Production & Brand Films",
      "Professional Shoots & Asset Creation",
    ],
  },
  {
    id: "print-media",
    icon: Printer,
    title: "Print & Media",
    desc: "Premium print solutions, outdoor branding, promotional creatives, and offline marketing campaigns.",
    bullets: [
      "Print & Outdoor (OOH) Systems",
      "Premium Print Media Layouts",
      "Corporate Collateral Systems",
    ],
  },
];

function ServiceCard({ s, idx }) {
  const Icon = s.icon;
  const { ref } = useScrollReveal({ threshold: 0.2 });
  return (
    <article
      ref={ref}
      data-testid={`service-card-${s.id}`}
      style={{ transitionDelay: `${idx * 140}ms` }}
      className="reveal-up group relative rounded-2xl border border-black/10 bg-white p-8 sm:p-10 overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-[#2770FD] hover:shadow-[0_20px_60px_-20px_rgba(39,112,253,0.45)]"
    >
      <div className="relative">
        <div className="flex items-center justify-between">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#2770FD]/10 border border-[#2770FD]/20 text-[#2770FD] transition-transform duration-500 group-hover:rotate-6 group-hover:scale-105">
            <Icon className="h-5 w-5" />
          </span>
          <span
            data-testid={`service-card-number-${s.id}`}
            className="font-display text-[#2770FD] text-xl tracking-[0.2em]"
            aria-hidden
          >
            0{idx + 1}
          </span>
        </div>

        <h3 className="mt-8 font-display text-black text-2xl sm:text-3xl leading-tight">
          {s.title}
        </h3>
        <p className="mt-4 text-black/70 text-sm sm:text-base font-light leading-relaxed">
          {s.desc}
        </p>

        <ul className="mt-8 space-y-3 border-t border-black/10 pt-6">
          {s.bullets.map((b, bi) => (
            <li
              key={b}
              className="flex items-start gap-3 text-sm text-black"
            >
              <span
                style={{ "--idx": bi }}
                className="check-pulse-host mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#2770FD]"
              >
                <Check className="h-3 w-3 text-white" strokeWidth={3} />
              </span>
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <a
          href="mailto:Connect@dynamicxmedia.com?subject=Service%20Enquiry"
          data-testid={`service-card-cta-${s.id}`}
          className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2770FD] hover:text-black transition-colors"
        >
          Discuss this service
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
    </article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-28 sm:py-36 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-3xl">
          <span
            data-testid="services-label"
            className="inline-block text-xs uppercase tracking-[0.3em] font-semibold text-[#2770FD] mb-5"
          >
            What We Build
          </span>
          <h2
            data-testid="services-heading"
            className="font-display text-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            360° Marketing Solutions{" "}
            <span className="text-accent-blue">Built for High-Growth Brands.</span>
          </h2>
          <p className="mt-6 text-black/70 text-base sm:text-lg max-w-2xl font-light leading-relaxed">
            Three disciplines, one obsessive standard of craft. From the first
            paid click to the final printed sheet — engineered end-to-end.
          </p>
        </div>

        {/* Cards */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((s, idx) => (
            <ServiceCard key={s.id} s={s} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}
